import { useParams } from "react-router-dom";
import { useFetchFrontendSubCategoriesQuery, useFetchFrontendCategoriesQuery } from "../../stores/api/pages.api";
import { UserLayout } from "./components/layout/UserLayout";
import PageHero from "./components/PageHero";
import Container from "./components/Container";
import Text from "./components/Text";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import { buildEntityMeta } from "../../hooks/useFrontendMeta";

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const CategoryDetailSkeleton = () => (
  <UserLayout>
    <Container variant="narrow" className="py-10 px-4 lg:px-8">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    </Container>
  </UserLayout>
);

const prettifySlug = (slug = "") =>
  slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const CategoryDetail = () => {
  const { categorySlug } = useParams();
  const { data: subCategories, isLoading } = useFetchFrontendSubCategoriesQuery(categorySlug);
  const { data: categoriesList } = useFetchFrontendCategoriesQuery();

  if (isLoading) return <CategoryDetailSkeleton />;

  // Prefer category name from subcat include; fall back to categories list by slug
  // (covers categories with no subcategories); last resort = prettified slug.
  const category =
    subCategories?.[0]?.category ??
    (categoriesList ?? []).find((c) => c.slug === categorySlug);
  const categoryName = category?.name ?? prettifySlug(categorySlug);

  // Per-category (and sub-category) SEO meta: prefer admin-entered meta_*, fall back to derived values.
  const categoryMeta = buildEntityMeta(category, {
    fallbackTitle: `${categoryName} | Ukasha Enterprise`,
    fallbackDescription: `Explore ${categoryName} products at Ukasha Enterprise.`,
    fallbackKeywords: categoryName,
    ogImage: category?.image,
  });

  return (
    <UserLayout meta={categoryMeta}>
      <PageHero
        title={categoryName}
        crumbs={[
          { label: "Home", href: ROUTE_PATHS.HOME },
          { label: "Products", href: ROUTE_PATHS.PRODUCTS },
          { label: categoryName },
        ]}
      />

      <Container variant="narrow" className="py-10 px-4 lg:px-8">
        <Container
          variant="full"
          showEmptyState={!subCategories?.length}
          emptyMessage="No subcategories found for this category."
        >
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {(subCategories ?? []).map((sub) => (
              <Text
                key={sub.slug}
                is="a"
                href={ROUTE_PATHS.PRODUCTS_BY_SUBCATEGORY.replace(":subCategorySlug", sub.slug).replace(":categorySlug", categorySlug)}
                className="flex items-center justify-center border border-gray-200 rounded-lg px-4 py-5 text-sm font-medium text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors text-center"
              >
                {sub.name}
              </Text>
            ))}
          </div>
        </Container>
      </Container>
    </UserLayout>
  );
};

export default CategoryDetail;
